// @ts-check

import React, { useEffect, useRef } from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/snippets/javascript';
import { Toast } from 'primereact/toast';
import { Subject } from 'rxjs';
import { catchError, debounceTime, map } from 'rxjs/operators';

import { updateCursor, validateInput } from '../../helpers/aceEditor';
import onSelectionChange from '../../helpers/onSelectionChange';
import InfoBar from '../InfoBar/InfoBar';
import Members from '../Members/Members';
import Notifications from '../Notifications/Notifications';
import { SetVideoId } from '../../helpers/search';
import { useSelector, useDispatch } from 'react-redux';
import { curMag, cursorState, setLinerOwner } from '../../features/cursorSlice';
import {
  cursorFeature,
  lineOwnFeature,
  popUpBoxFeature,
  deletionAnimationFeature,
  deletionObj,
  updateFeatures,
} from '../../features/featuresSlice';
import AnimatedText from 'react-animated-text-content';

const random6LetterName = () => {
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  let name = '';
  for (let i = 0; i < 6; i++) {
    name += letters[Math.floor(Math.random() * letters.length)];
  }
  return name;
};

export const Editor = ({
  socket,
  search,
  searchResult,
  setSearchResult,
  videoId,
  room,
  lastedit,
  aceEditor,
  localUser,
  setLocalUser,
  code,
  player,
  notifications,
  allUsers,
  name,
}) => {
  const dispatch = useDispatch();

  const toast = useRef(null);
  const toast2 = useRef(null);
  const toast3 = useRef(null);

  // Create a subject to hold the last selection
  const lastSelectionSubject = new Subject();

  // Create a subject to hold the special command
  const specialCommandSubject = new Subject();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const deletedTextSubject = new Subject();

  const showToast = ({ summary, detail, severity = 'error' }) => {
    // @ts-ignore
    toast.current.show({
      severity: severity,
      summary: summary,
      detail: detail,
      life: 3000,
    });
  };

  useEffect(() => {
    socket.on('receiveSpecialCommand', ({ command, param }) => {
      console.log('receiveSpecialCommand', command, param);
      specialCommandSubject.next({ command, param });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // a function to check if the selection does not contains any command
  const checkSelection = (selection) => {
    const commands = [
      'search',
      'play',
      'pause',
      'stop',
      'mute',
      'fade',
      'unmute',
      'select',
      'clear',
      'seek',
      'loopAt',
      'delay',
    ];
    try {
      const content = aceEditor.current.editor.session.getTextRange(
        selection.getRange()
      );

      // check if the selected text is a command
      if (content.length < 4) {
        return false;
      }

      // if contains delay and loopAt command, show a toast
      if (content.includes('delay') || content.includes('loopAt')) {
        console.log('contains delay and loopAt');
        socket.emit('specialCommand', {
          command: content.includes('delay') ? 'delay' : 'loopAt',
          param: {
            user: localUser,
          },
        });
      }

      // if the selection contains some words, return null else call the showToast function
      if (commands.some((command) => content.includes(command))) {
        return true;
      }

      showToast({
        summary: 'Invalid Command',
        detail: 'Please select a valid command',
      });

      return false;
    } catch (error) {
      return false;
    }
  };

  // Subscribe to the subject to get the special command
  specialCommandSubject
    .pipe(debounceTime(1000))
    .subscribe(({ command, param }) => {
      showToast({
        summary: 'Special Command',
        severity: 'info',
        detail: `${param.user.name} just used the ${command} command`,
      });
    });

  // Subscribe to the subject to get the last selection
  lastSelectionSubject
    .pipe(
      debounceTime(2000),
      map((e) => {
        // const content = aceEditor.current.editor.session.getTextRange(
        //   e.getRange()
        // );

        // console.log('content', content);
        return e;
      }),

      catchError((error) => {
        console.error('Error:', error);
        return error;
      })
    ) // Debounce the stream to handle rapid selection changes
    .subscribe(
      (lastSelection) => {
        // Handle the last selection here
        // console.log('Last Selection:', lastSelection);

        // update the cursor position
        updateCursor(aceEditor, socket, curMgr, localUser, cursorFeat);

        if (popUpBoxFeat) {
          // check if the selection contains any command
          if (!checkSelection(lastSelection)) {
            return;
          }
        }

        // call the onSelectionChange function
        onSelectionChange(lastSelection, aceEditor, player, socket);
      },
      (error) => {
        // Handle errors in the subscription
        console.error('Error:', error);
      }
    );

  const curMgr = useSelector(curMag);

  const cursorStates = useSelector(cursorState);

  const cursorFeat = useSelector(cursorFeature);

  const lineOwnFeat = useSelector(lineOwnFeature);

  const popUpBoxFeat = useSelector(popUpBoxFeature);

  const deletionAnimationFeat = useSelector(deletionAnimationFeature);

  const deletionObjFeat = useSelector(deletionObj);

  const updateFeat = ({ type, state }) => {
    return dispatch(updateFeatures({ type, state }));
  };

  const updateLineOwner = (owner) => {
    return dispatch(setLinerOwner(owner));
  };

  const deletedTextSubscription = deletedTextSubject
    .pipe(debounceTime(200))
    .subscribe((lastSelection) => {
      // @ts-ignore
      toast2.current.show({
        severity: 'info',
        content: (
          <div className='tos'>
            <p>Deleted by {lastSelection.user.name}</p>

            <AnimatedText
              type='chars' // animate words or chars
              animation={{
                x: '200px',
                y: '-20px',
                scale: 1.1,
                ease: 'ease-in-out',
              }}
              animationType='rifle'
              interval={0.06}
              duration={0.4}
              tag='p'
              className='animated-paragraph'
              includeWhiteSpaces
              threshold={0.1}
              rootMargin='20%'
            >
              {lastSelection.body}
            </AnimatedText>
          </div>
        ),
        life: 8000,
        styleClass: 'tos',
      });

      updateFeat({ type: 'deletionObj', state: null });
    });

  useEffect(() => {
    if (deletionObjFeat === null) {
      return;
    }
    deletedTextSubject.next(deletionObjFeat);
    return () => {
      deletedTextSubscription.unsubscribe();
    };
  }, [deletedTextSubject, deletedTextSubscription, deletionObjFeat]);

  const handleTextChange = (value, event) => {
    if (!deletionAnimationFeat) {
      return;
    }
    if (event.action === 'remove') {
      if (event.lines.length >= 2) {
        // console.log(event.lines);
        // console.log(localUser);
        socket.emit('deletedCommand', {
          user: localUser,
          body: event.lines.join('\n'),
        });
      }
    }
  };

  return (
    <div className='container'>
      <div className='row'>
        <div className='col' style={{ marginTop: 20 }}>
          <div className='card p-3 logincard' style={{ width: '35rem' }}>
            {
              /* THIS IS WHERE I WANT TO INCLUDE SOUND CONTROL PANEL OR MOST LIKELY 
                          ABOVE THE CARD COLUMN UNDER ROW*/
              //
              // onClick={() => handlePlaySound() }
              // <button
              //   style={{ margin: 10 }}
              //   onClick={() => {
              //     socket.emit('playerCommand', {
              //       command: 'play',
              //       param: null,
              //     });
              //   }}
              // >
              //   Play{' '}
              // </button>
            }
            {
              // <button
              //   style={{ margin: 10 }}
              //   onClick={() => {
              //     socket.emit('playerCommand', {
              //       command: 'pause',
              //       param: null,
              //     });
              //   }}
              // >
              //   pause{' '}
              // </button>
            }
            {
              // <button
              //   style={{ margin: 10 }}
              //   onClick={() => {
              //     socket.emit('playerCommand', {
              //       command: 'stop',
              //       param: null,
              //     });
              //   }}
              // >
              //   Stop{' '}
              // </button>
            }
            {
              <div className=''>
                <label id='search'>Search Keyword</label>
                <input
                  id='search'
                  style={{ margin: 20 }}
                  type='text'
                  value={search}
                  onChange={(e) => {
                    socket.emit('sendCommand', {
                      command: 'searchKey',
                      param: e.target.value,
                    });
                  }}
                />
                <button
                  style={{ margin: 20 }}
                  onClick={() => {
                    socket.emit('sendCommand', {
                      command: 'search',
                      param: search,
                    });
                  }}
                >
                  Search{' '}
                </button>
              </div>
            }
            {
              <div className='row'>
                {searchResult.map((item) => {
                  return (
                    <div
                      key={item.id.videoId + random6LetterName()}
                      id={item.id.videoId}
                      className='col-2'
                      style={{ margin: 10, cursor: 'pointer' }}
                      // setting the selected video
                      onClick={(e) => SetVideoId(e, socket, setSearchResult)}
                    >
                      <img
                        src={item.snippet.thumbnails.default.url}
                        alt='youtube'
                        id={item.id.videoId}
                        width={100}
                        height={50}
                      />
                    </div>
                  );
                })}
              </div>
            }

            {videoId.map((v) => {
              return <div key={v + random6LetterName()} id={v}></div>;
            })}

            <InfoBar room={room} lastedit={lastedit} />
            <div className='wrapped-editor'>
              <Toast ref={toast} position='top-left' />
              <Toast ref={toast2} position='top-right' />
              <Toast ref={toast3} position='top-center' />
              <AceEditor
                mode='javascript'
                ref={aceEditor}
                theme='monokai'
                value={code}
                onCursorChange={(value, event) => {
                  updateCursor(
                    aceEditor,
                    socket,
                    curMgr,
                    localUser,
                    cursorFeat
                  );
                }}
                onChange={(value, event) => {
                  //console.log(cursorStates);
                  //console.log(localUser);
                  if (deletionAnimationFeat) {
                    handleTextChange(value, event);
                  }
                  // check if the cursor is in another user's cursor from the cursorstates if it is, then don't allow the user to edit
                  const { row } = aceEditor.current.editor.getCursorPosition();
                  const cur = cursorStates.find(
                    (c) => c.userRow === row && c.user !== localUser.name
                  );
                  if (lineOwnFeat) {
                    // console.log('cursor is in another user', cur);
                    // console.log('cursor is in another user', lineOwnFeat);
                    if (cur) {
                      updateLineOwner(cur);
                    }
                  }
                  validateInput(
                    value,
                    aceEditor,
                    socket,
                    curMgr,
                    localUser,
                    cursorFeat
                  );
                }} //onChange it should do validateInput function(which
                style={{ width: '100%', minHeight: '700px' }} // is to emit sendCode with our aceEditor value)
                setOptions={{
                  enableLiveAutocompletion: true,
                }}
                onSelectionChange={(selection, event) => {
                  // console.log(selection);
                  lastSelectionSubject.next(selection);
                }}
                className='editor'
                highlightActiveLine={true}
              />
              <div className='radar-view' id='radar-view'></div>
            </div>
          </div>
        </div>
        <div className='col' style={{ marginTop: 20, marginLeft: '100px' }}>
          <div className='card p-3 mb-3'>
            <h5 style={{ textAlign: 'center' }}>Commands Details</h5>

            <code className='card p-3 scroll'>
              1. play(0): parameter is the player position. This command plays
              the video
              <br />
              2. pause(0): parameter is the player position. This command pause
              the video
              <br />
              3. stop(0): parameter is the player position. This command stop
              the video
              <br />
              4. mute(0): parameter is the player position. This command mute
              the video
              <br />
              5. unmute(0): parameter is the player position. This command
              unmute the video
              <br />
              6. loopAt(0,20,30): Has three parameter which are as follows
              [1.]player position, [2.]where the player should start the loop
              from in seconds, [3.]where the player should end the loop in
              seconds
              <br />
              7. delay(0,0.5): Has two parameter which are as follows [1.]player
              position, [2.]playback rate of the video which can only be
              0.5,1.0,1.5,2.0,2.5
              <br />
              8. fade(0,20): Has two parameter which are as follows [1.]player
              position, [2.] the volume of the player in percentage
              <br />
              9. seek(0,40): Has two parameter which are as follows [1.]player
              position, [2.] seconds you want to seek to(fast forward) in the
              selected video
              <br />
              10. select(0): parameter is the position of the video you want to
              select.
            </code>
          </div>

          <div className='card p-3'>
            <h5 style={{ textAlign: 'center' }}>Notifications</h5>

            <div className='scroll'>
              <Notifications notifications={notifications} name={name} />
            </div>
          </div>
          <div className='card p-3' style={{ marginTop: '20px' }}>
            <h5 style={{ textAlign: 'center' }}>Members</h5>
            <div className='scroll'>
              <Members users={allUsers} />
            </div>
            <br />
            <br />
            <a className='btn btn-danger' href='/'>
              Leave
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
